// Wire-format status values for Medical Staff — the UPPERCASE enum code the API emits
// (3-state model). Korean display labels resolve from the backend-served label catalog at render time.
export type MedicalStaffStatus = 'PENDING' | 'ACTIVE' | 'WITHDRAWN';

export const MEDICAL_STAFF_STATUSES: readonly MedicalStaffStatus[] = [
  'PENDING',
  'ACTIVE',
  'WITHDRAWN',
] as const;

export interface MedicalStaff {
  // v9 (DEC-DB-PK-BIGINT-001): wire id is BIGINT UNSIGNED → number.
  // URL/route params (React Router) remain strings; parse with Number() at the call site.
  id: number;
  name: string;
  email: string;
  // Null when not provided on invite; rendered as "-" in the table (PRD §3.1).
  phone: string | null;
  institution: string;
  // Canonical seed values (e.g. '정신건강의학과'). Extensible via the options pool.
  department: string;
  // Canonical seed values: '의사' | '간호사' | 'CRC' (PRD §3.1). Extensible via the options pool.
  jobRole: string;
  // ISO datetime; null while status === 'PENDING' — rendered as "-".
  registeredAt: string | null;
  status: MedicalStaffStatus;
  // PRD 03 §3.8 (v1.3): a pending staff-initiated self-withdraw request.
  // Present only while the staff has requested withdrawal and an admin has not yet
  // approved/rejected it; null/absent otherwise. The list row shows a chip and the
  // edit panel swaps in Approve/Reject when this is set.
  // Wire note: admin-api sends a FLAT `withdrawRequestedAt` string; the HTTP adapter
  // maps it into this nested shape so the UI has one consistent field.
  withdrawRequest?: { requestedAt: string } | null;
}

export type MedicalStaffSortKey =
  | 'id'
  | 'name'
  | 'email'
  | 'department'
  | 'jobRole'
  | 'registeredAt';

export type SortDirection = 'asc' | 'desc';

export interface ListMedicalStaffParams {
  // Partial match across name OR email OR department, case-insensitive (PRD §3.2).
  q?: string;
  // Exact match on Department column.
  department?: string;
  status?: MedicalStaffStatus;
  page?: number;
  perPage?: number;
  sortBy?: MedicalStaffSortKey;
  sortDir?: SortDirection;
}

export interface ListMedicalStaffResult {
  items: MedicalStaff[];
  total: number;
  page: number;
  perPage: number;
}

// CSV export uses the same filter + sort state as the list, but bypasses pagination
// so the file contains every matching row (PRD 03 §3.4).
export type ExportMedicalStaffParams = Pick<
  ListMedicalStaffParams,
  'q' | 'department' | 'status' | 'sortBy' | 'sortDir'
>;

export interface InviteMedicalStaffInput {
  name: string;
  email: string;
  phone: string | null;
  department: string;
  jobRole: string;
}

// PRD §3.6: email is immutable across all statuses, so it is not part of the update body.
export interface UpdateMedicalStaffInput {
  name?: string;
  phone?: string | null;
  department?: string;
  jobRole?: string;
  status?: MedicalStaffStatus;
}

export interface MedicalStaffOptions {
  departments: string[];
  jobRoles: string[];
}

export interface AddOptionInput {
  name: string;
}

export type MedicalStaffApiErrorCode =
  | 'EMAIL_DUPLICATE'
  | 'EMAIL_INVALID'
  | 'PHONE_INVALID'
  | 'PHONE_DUPLICATE'
  | 'NAME_INVALID'
  | 'DEPARTMENT_REQUIRED'
  | 'ROLE_REQUIRED'
  | 'INVALID_TARGET_STATUS'
  | 'EMAIL_SEND_FAILED'
  | 'OPTION_DUPLICATE'
  | 'OPTION_INVALID'
  | 'STAFF_WITHDRAWN_RESET_FORBIDDEN'
  | 'STAFF_CREDENTIAL_MISSING'
  | 'SELF_WITHDRAW_FORBIDDEN'
  | 'INSUFFICIENT_PERMISSION'
  | 'NOT_FOUND'
  | 'UNKNOWN';

export class MedicalStaffApiError extends Error {
  readonly code: MedicalStaffApiErrorCode;
  readonly fields?: Record<string, string>;
  readonly meta?: Record<string, unknown>;

  constructor(
    code: MedicalStaffApiErrorCode,
    message: string,
    options?: { fields?: Record<string, string>; meta?: Record<string, unknown> },
  ) {
    super(message);
    this.name = 'MedicalStaffApiError';
    this.code = code;
    this.fields = options?.fields;
    this.meta = options?.meta;
    Object.setPrototypeOf(this, MedicalStaffApiError.prototype);
  }
}

export interface MedicalStaffApi {
  listMedicalStaff(params: ListMedicalStaffParams): Promise<ListMedicalStaffResult>;
  // Unpaginated read of every row that matches the current filter+sort — feeds the
  // client-side CSV generator (PRD 03 §3.4).
  exportMedicalStaff(params: ExportMedicalStaffParams): Promise<MedicalStaff[]>;
  inviteMedicalStaff(input: InviteMedicalStaffInput): Promise<MedicalStaff>;
  resendInvitation(id: number): Promise<void>;
  updateMedicalStaff(id: number, input: UpdateMedicalStaffInput): Promise<MedicalStaff>;
  withdrawMedicalStaff(id: number): Promise<MedicalStaff>;
  // PRD 03 §3.8 (v1.3): approve a staff-initiated self-withdraw request.
  // Server flips status → WITHDRAWN and clears the request.
  approveWithdrawMedicalStaff(id: number): Promise<MedicalStaff>;
  // PRD 03 §3.8 (v1.3): reject a staff-initiated self-withdraw request.
  // Server clears the request only; status stays unchanged.
  rejectWithdrawMedicalStaff(id: number): Promise<MedicalStaff>;
  listOptions(): Promise<MedicalStaffOptions>;
  // PRD 03 §3.7 (v1.2): server generates a temporary password (en+digit+symbol,
  // ≥10 chars) and emails it to the staff member. Only valid for Registered rows.
  // Returns 204; FE shows a toast and routes back to the list.
  resetPassword(id: number): Promise<void>;
  listOptions(): Promise<MedicalStaffOptions>;
  // PRD 03 §3.3 (v1.2 / Q13): department + job-role options are DB-preloaded
  // and globally shared. These mutations are retained for API parity but no
  // FE caller invokes them after PRD 03 v1.2 (marked deprecated in api-contract).
  addDepartmentOption(input: AddOptionInput): Promise<MedicalStaffOptions>;
  addJobRoleOption(input: AddOptionInput): Promise<MedicalStaffOptions>;
}
